import { memo } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';

import type { MaterialMovementReport } from '../../../../redux/api/material-movements';
import { ACTION_META, ACTION_ORDER } from '../../../../components/MaterialMovements/movementMeta';
import type { AuditLine, AuditSection } from './auditLines';
import { MovementFilters, formatRange } from './filters';

interface Option<T> {
	value: T;
	label: string;
}

interface PrintableMovementReportProps {
	data: MaterialMovementReport;
	sections: AuditSection[];
	filters: MovementFilters;
	operatorOptions: Option<number>[];
	buildingOptions?: Option<string>[];
}

/**
 * Inches on landscape Letter (10.2in printable); Job / notes takes what is left (about 2in).
 * Sized so "10:20 AM – 11:05 AM", `144.00" → 100.00"` and "Returned from job" fit on one line at 10pt.
 */
const COLUMNS: { label: string; width?: string; className?: string }[] = [
	{ label: 'Verified', width: '0.55in', className: 'movement-print__check' },
	{ label: 'Bars', width: '1.2in' },
	{ label: 'When', width: '1.55in' },
	{ label: 'Activity', width: '1.25in' },
	{ label: 'Length', width: '1.3in' },
	{ label: 'Location', width: '1.35in' },
	{ label: 'Operator', width: '1in' },
	{ label: 'Job / notes' },
];

const describeFilters = (
	filters: MovementFilters,
	operatorOptions: Option<number>[],
	buildingOptions: Option<string>[]
) => {
	const names = <T,>(ids: T[], options: Option<T>[]) =>
		ids.map((id) => options.find((o) => o.value === id)?.label ?? String(id)).join(', ');
	const parts: string[] = [];
	if (filters.performedBy.length) {
		parts.push(`Operators: ${names(filters.performedBy, operatorOptions)}`);
	}
	if (filters.actions.length) {
		parts.push(`Actions: ${filters.actions.map((a) => ACTION_META[a].label).join(', ')}`);
	}
	if (filters.buildings.length) {
		parts.push(`Buildings: ${names(filters.buildings, buildingOptions)}`);
	}
	if (filters.bar) parts.push(`Bar: ${filters.bar}`);
	return parts.length ? parts.join(' · ') : 'All operators, actions and buildings';
};

const AuditRow = ({ line }: { line: AuditLine }) => (
	<tr className="movement-print__line">
		<td className="movement-print__check">
			<span className="movement-print__box" />
		</td>
		<td className="movement-print__bar-id movement-print__wrap">{line.barIds}</td>
		<td>{line.timeSpan}</td>
		<td>{line.activity}</td>
		<td>{line.length}</td>
		<td>
			{line.location}
			{line.nowAt && <strong> ({line.nowAt})</strong>}
		</td>
		<td>{line.operators}</td>
		<td className="movement-print__wrap">
			{[line.notes, line.historical && '(historical)'].filter(Boolean).join(' · ')}
		</td>
	</tr>
);

const PrintableMovementReport = ({
	data,
	sections,
	filters,
	operatorOptions,
	buildingOptions = [],
}: PrintableMovementReportProps) => {
	const printedAt = dayjs().format('MMM D, YYYY h:mm A');
	const range = formatRange(data.date_from, data.date_to);
	const actionsWithData = data.summary.by_action.filter((row) => row.transactions > 0);
	const operatorActions = ACTION_ORDER.filter((action) =>
		data.operators.some((op) => op.by_action[action])
	);

	return createPortal(
		<div className="movement-print">
			<table className="movement-print__page">
				<thead>
					<tr>
						<td className="movement-print__header">
							<div className="movement-print__title">
								Material Movement Audit — {range}
							</div>
							<div className="movement-print__meta">
								<span>{describeFilters(filters, operatorOptions, buildingOptions)}</span>
								<span>Central time · printed {printedAt}</span>
							</div>
						</td>
					</tr>
				</thead>
				<tfoot>
					<tr>
						<td className="movement-print__footer">
							PartsBadger · Material Movements · {range} · Tick “Verified” when every bar on
							the line is physically at the listed location and length.
						</td>
					</tr>
				</tfoot>
				<tbody>
					<tr>
						<td className="movement-print__body">
							<div className="movement-print__summary">
								<strong>
									{data.summary.transactions} movements · {data.summary.bars_touched}{' '}
									bars · {data.summary.operators} operators
								</strong>
								{actionsWithData.length > 0 && (
									<span>
										{' — '}
										{actionsWithData
											.map((row) => `${ACTION_META[row.action]?.label ?? row.label}: ${row.transactions}`)
											.join(' · ')}
									</span>
								)}
							</div>

							{data.truncated && (
								<p className="movement-print__warning">
									List cut off at the first {data.row_cap.toLocaleString()} movements; the
									counts above cover everything. Narrow the range to print the rest.
								</p>
							)}

							{data.operators.length > 0 && (
								<table className="movement-print__data movement-print__operators">
									<thead>
										<tr>
											<th>Operator</th>
											{operatorActions.map((action) => (
												<th key={action} className="movement-print__num">
													{ACTION_META[action].label}
												</th>
											))}
											<th className="movement-print__num">Total</th>
										</tr>
									</thead>
									<tbody>
										{data.operators.map((op) => (
											<tr key={String(op.id)}>
												<td>{op.name}</td>
												{operatorActions.map((action) => (
													<td key={action} className="movement-print__num">
														{op.by_action[action] ?? '—'}
													</td>
												))}
												<td className="movement-print__num">{op.total}</td>
											</tr>
										))}
									</tbody>
								</table>
							)}

							{sections.length === 0 && (
								<p>No material movements were logged for this range and filter.</p>
							)}

							{sections.length > 0 && (
								<table className="movement-print__data movement-print__audit">
									<colgroup>
										{COLUMNS.map((col) => (
											<col key={col.label} style={col.width ? { width: col.width } : undefined} />
										))}
									</colgroup>
									<thead>
										<tr>
											{COLUMNS.map((col) => (
												<th key={col.label} className={col.className}>
													{col.label}
												</th>
											))}
										</tr>
									</thead>
									{sections.map((section) => (
										<tbody key={section.key} className="movement-print__section">
											<tr>
												<th colSpan={COLUMNS.length} className="movement-print__material">
													{section.materialName}
													<span className="movement-print__count">
														{' '}
														· {section.barCount} {section.barCount === 1 ? 'bar' : 'bars'}
													</span>
												</th>
											</tr>
											{section.lines.map((line) => (
												<AuditRow key={line.key} line={line} />
											))}
										</tbody>
									))}
								</table>
							)}

							<div className="movement-print__signoff">
								<span>Audited by: ______________________________</span>
								<span>Date: ______________</span>
								<span>Signature: ______________________________</span>
							</div>
						</td>
					</tr>
				</tbody>
			</table>
		</div>,
		document.body
	);
};

const MemoizedPrintableMovementReport = memo(PrintableMovementReport);

export default MemoizedPrintableMovementReport;
